import GlobalContext from '../../contexts/GlobalContext';
import PasswordInput from '../PasswordInput/PasswordInput';
import PasswordValidator from '../PasswordValidator/PasswordValidator';
import LegalAgreements from '../LegalAgreements/LegalAgreements';
import styles from './ConfirmAccountForm.module.scss';
import common from '../../util/scss/common.module.scss';
import ConfirmAccountContext, { EConfirmAccountUiState } from '../../contexts/ConfirmAccountContext';
import CoBranding from '../CoBranding/CoBranding';
import BackCardLayout from '../../layout/BackCardLayout/BackCardLayout';
import { FC, useContext } from 'react';
import { Alert, Box, MenuItem, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import PageLayout from '../../layout/PageLayout/PageLayout';

const ConfirmAccountFormUi: FC = () => {
    const { language, countriesList, coBranding } = useContext(GlobalContext);
    const {
        confirmAccountUiState,
        firstName,
        setFirstName,
        firstNameIsValid,
        firstNameValidationMessage,
        lastName,
        setLastName,
        lastNameIsValid,
        lastNameValidationMessage,
        email,
        password,
        setPassword,
        passwordIsValid,
        countryCode,
        setCountryCode,
        countryIsValid,
        submitConfirmAccount,
        errorMessage,
        familyFirstAddFamily,
    } = useContext(ConfirmAccountContext);

    const handleSubmit = (event) => {
        event.preventDefault();
        coBranding.isDefaultFamilyAccount ? familyFirstAddFamily() : submitConfirmAccount();
    };

    return (
        <>
            {(coBranding.logoUrl || coBranding.name) && (
                <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
            )}
            <header>
                {coBranding.isDefaultFamilyAccount ? (
                    <>
                        <strong className={common.progressHeader}>{`${language.common.step} 2`}</strong>
                        <h1 className={`${common.pageHeader} ${common.withProgress}`}>
                            {coBranding.isDefaultFamilyAccount
                                ? language.confirmAccount.title
                                : language.confirmAccount.family.familyFirstTitle}
                        </h1>
                    </>
                ) : (
                    <h1 className={common.pageHeader}>{language.createAccount.header}</h1>
                )}
            </header>
            {confirmAccountUiState === EConfirmAccountUiState.softError && (
                <section className={common.genericError}>
                    <Alert severity="error">{errorMessage}</Alert>
                </section>
            )}
            <form data-testid="confirm-account-form">
                <main className={styles.confirmAccountForm}>
                    <TextField
                        label={language.createAccount.form.firstName}
                        value={firstName}
                        error={!firstNameIsValid}
                        helperText={!firstNameIsValid && firstNameValidationMessage}
                        onChange={(event) => setFirstName(event.target.value)}
                        autoFocus={coBranding.isDefaultFamilyAccount ? true : undefined}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label={language.createAccount.form.lastName}
                        value={lastName}
                        error={!lastNameIsValid}
                        helperText={!lastNameIsValid && lastNameValidationMessage}
                        onChange={(event) => setLastName(event.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label={language.createAccount.form.email}
                        value={email}
                        disabled={true}
                        fullWidth
                        margin="normal"
                    />
                    <PasswordInput
                        value={password}
                        label={language.createAccount.form.password}
                        onChange={(event) => setPassword(event.target.value)}
                        autoFocus={!coBranding.isDefaultFamilyAccount ? true : undefined}
                        error={!passwordIsValid}
                        helperText={!passwordIsValid && language.validation.password.invalid}
                    />
                    <Box sx={{ my: 2 }}>
                        <PasswordValidator password={password} />
                    </Box>
                    <TextField
                        select
                        label={language.createAccount.form.country}
                        value={countryCode}
                        onChange={(event) => setCountryCode(event.target.value)}
                        error={!countryIsValid}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        {countriesList.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <LoadingButton
                        variant="contained"
                        type="submit"
                        disabled={confirmAccountUiState === EConfirmAccountUiState.loading}
                        onClick={handleSubmit}
                        loading={confirmAccountUiState === EConfirmAccountUiState.loading}
                        size="large"
                    >
                        {language.createAccount.createAccountCTA}
                    </LoadingButton>
                    <Box sx={{ my: 2 }}>
                        <LegalAgreements />
                    </Box>
                </main>
            </form>
        </>
    );
};

const ConfirmAccountForm: FC = () => {
    const { coBranding } = useContext(GlobalContext);
    return (
        <>
            {coBranding.isDefaultFamilyAccount ? (
                <PageLayout showHeader={false}>
                    <BackCardLayout>
                        <ConfirmAccountFormUi />
                    </BackCardLayout>
                </PageLayout>
            ) : (
                <PageLayoutV2>
                    <ConfirmAccountFormUi />
                </PageLayoutV2>
            )}
        </>
    );
};

export default ConfirmAccountForm;
